import { useRef, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideHover } from "./useTourGuideHover";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";

/**
 * Tour Guide Hook for Course Detail Page (Free Courses)
 *
 * Guides users through the course details:
 * - Enrollment period information
 * - Course chapters overview
 * - Video lessons within chapters
 */
export const useCourseDetailTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();

  // Create refs for elements that need them
  const enrollmentPeriodRef = useRef<HTMLDivElement>(null);
  const chaptersHeaderRef = useRef<HTMLDivElement>(null);

  // Detect if we're on a course detail route
  const isOnCourseDetail = pathname.includes(
    ROUTE_NAMES.UNAUTHENTICATED_COURSE_DETAIL,
  );

  // Define tour configuration
  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "courseDetail",
      enabled: isOnCourseDetail,
      steps: [
        // Enrollment Period Step
        {
          id: "enrollment-period",
          targetRef: enrollmentPeriodRef,
          title: t("tourGuide.courseDetail.step1.title"),
          description: t("tourGuide.courseDetail.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        // Chapter Name Step
        {
          id: "chapter-name",
          targetRef: chaptersHeaderRef,
          title: t("tourGuide.courseDetail.step2.title"),
          description: t("tourGuide.courseDetail.step2.description"),
          position: "right",
          offset: { x: 15, y: 0 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnCourseDetail, t],
  );

  // Create base tour guide
  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.UnauthenticatedCourseDetail, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const courseDetailTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.UnauthenticatedCourseDetail,
    isOnTourPath: isOnCourseDetail,
    onTourComplete: handleTourComplete,
  });

  // Sync with tour guide context
  useEffect(() => {
    if (courseDetailTourGuide.isActive) {
      setActiveTourModule("courseDetail");
      setCurrentTourStep(courseDetailTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    courseDetailTourGuide.isActive,
    courseDetailTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  // Create hover tooltip instances for each step
  const enrollmentPeriodHover = useTourGuideHover(
    "enrollment-period",
    config.steps[0],
  );
  const chapterNameHover = useTourGuideHover("chapter-name", config.steps[1]);

  return {
    courseDetailTourGuide,
    // Refs to attach to elements
    enrollmentPeriodRef,
    chaptersHeaderRef,
    // Hover tooltip instances
    enrollmentPeriodHover,
    chapterNameHover,
  };
};
